import { useState, useEffect, useRef } from 'react';

const API = process.env.REACT_APP_API_URL || '';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Alta self-service de un boliche. Se llega desde la raíz (tukipro.com) o /signup.
export default function Signup() {
  const [orgName, setOrgName] = useState('');
  const [slug, setSlug] = useState('');
  const [touchedSlug, setTouchedSlug] = useState(false);
  const [check, setCheck] = useState(null); // { available, normalized, reason }
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [responsable, setResponsable] = useState('');
  // Documento y teléfono del titular: con el email solo no se puede facturar ni
  // reclamar nada. Lo fiscal (CUIT, domicilio) se pide después, desde el panel.
  const [dni, setDni] = useState('');
  const [telefono, setTelefono] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // pantalla de éxito con la URL de acceso
  const gbtn = useRef(null);
  const timer = useRef(null);

  // Si no tocó el subdominio a mano, lo derivo del nombre del boliche.
  useEffect(() => {
    if (!touchedSlug) setSlug(orgName);
  }, [orgName]); // eslint-disable-line

  // Chequeo de disponibilidad en vivo (debounce).
  useEffect(() => {
    setCheck(null);
    if (!slug.trim()) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/public/signup/slug-available?slug=${encodeURIComponent(slug)}`);
        setCheck(await r.json());
      } catch { /* noop */ }
    }, 400);
    return () => clearTimeout(timer.current);
  }, [slug]);

  function finish(data) {
    if (data.token) localStorage.setItem('tk_token', data.token);
    // Cada boliche opera en SU subdominio. Mostramos su URL de acceso (el token no cruza
    // de origen, así que no auto-redirijo: que la guarde y entre por su subdominio).
    const s = data.org?.slug || check?.normalized || slug;
    const host = (window.location.hostname || '').toLowerCase();
    const onPlatform = host === 'tukipro.com' || host === 'www.tukipro.com' || host.endsWith('.tukipro.com');
    const adminUrl = onPlatform ? `${window.location.protocol}//${s}.tukipro.com/admin` : '/admin';
    setDone({ slug: s, name: data.org?.name || orgName, adminUrl, needsVerification: data.needsVerification, email: data.user?.email || email.trim() });
  }

  async function submit() {
    setErr('');
    if (!orgName.trim()) { setErr('Poné el nombre del boliche'); return; }
    if (!check?.available) { setErr('Elegí un subdominio disponible'); return; }
    if (!email.trim() || password.length < 6) { setErr('Email y contraseña (mín. 6) requeridos'); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/public/signup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ org_name: orgName.trim(), slug, responsable: responsable.trim() || null, titular_dni: dni, titular_telefono: telefono, email: email.trim(), password }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.error || 'No se pudo crear la cuenta'); return; }
      finish(d);
    } catch { setErr('Error de conexión'); } finally { setLoading(false); }
  }

  // Botón de Google (alta con cuenta Google → entra verificado).
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const onCredential = async (resp) => {
      setErr('');
      if (!orgName.trim() || !check?.available) { setErr('Completá el nombre y el subdominio antes de usar Google'); return; }
      setLoading(true);
      try {
        const r = await fetch(`${API}/api/public/signup`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ org_name: orgName.trim(), slug, responsable: responsable.trim() || null, titular_dni: dni, titular_telefono: telefono, credential: resp.credential }),
        });
        const d = await r.json();
        if (!r.ok) { setErr(d.error || 'No se pudo crear la cuenta con Google'); return; }
        finish(d);
      } catch { setErr('Error de conexión'); } finally { setLoading(false); }
    };
    const render = () => {
      if (!window.google?.accounts?.id || !gbtn.current) return;
      window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: onCredential });
      gbtn.current.innerHTML = '';
      window.google.accounts.id.renderButton(gbtn.current, { theme: 'filled_black', size: 'large', shape: 'pill', text: 'signup_with', width: 320 });
    };
    if (window.google?.accounts?.id) { render(); return; }
    let s = document.getElementById('gsi-script');
    if (!s) { s = document.createElement('script'); s.id = 'gsi-script'; s.src = 'https://accounts.google.com/gsi/client'; s.async = true; s.defer = true; document.body.appendChild(s); }
    s.addEventListener('load', render);
    return () => s.removeEventListener('load', render);
  }, [orgName, slug, responsable, dni, telefono, check]); // eslint-disable-line

  const preview = check?.normalized || '';

  if (done) return <SignupDone {...done} />;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 20px' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 24, letterSpacing: '0.1em', color: '#fff' }}>TUKIPRO</div>
      <div style={{ color: 'var(--ink-700)', fontSize: 14, marginTop: 8, marginBottom: 28, textAlign: 'center', maxWidth: 460 }}>
        Creá tu boliche en minutos. Eventos, venta online, RRPP, TPV, inventario y contabilidad en un solo lugar. Probalo gratis 30 días.
      </div>

      <div className="lb-card" style={{ width: '100%', maxWidth: 460, padding: '28px 26px' }}>
        <div className="lb-eyebrow" style={{ marginBottom: 16 }}>Crear cuenta</div>

        <Field label="Nombre del boliche">
          <input className="lb-input" value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="Ej: Tu Boliche" />
        </Field>

        <div className="lb-field">
          <div className="lb-label">Subdominio</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <input className="lb-input" style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }} value={slug}
              onChange={e => { setTouchedSlug(true); setSlug(e.target.value); }} placeholder="tuboliche" />
            <span style={{ background: 'var(--ink-100)', border: '1px solid var(--border)', borderLeft: 0, padding: '0 12px', height: 42, display: 'flex', alignItems: 'center', color: 'var(--ink-600)', fontSize: 13, borderRadius: '0 4px 4px 0' }}>.tukipro.com</span>
          </div>
          {check && (check.available
            ? <div style={{ fontSize: 12, color: '#2ecc71', marginTop: 6 }}>✓ {preview}.tukipro.com está disponible</div>
            : <div style={{ fontSize: 12, color: 'var(--red-300)', marginTop: 6 }}>✗ {check.reason}{preview && check.reason !== 'Ese subdominio ya está en uso' ? ` (quedaría ${preview})` : ''}</div>)}
        </div>

        <Field label="Nombre y apellido del titular">
          <input className="lb-input" value={responsable} onChange={e => setResponsable(e.target.value)} placeholder="Juan Pérez" />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="DNI del titular">
            <input className="lb-input" inputMode="numeric" value={dni} onChange={e => setDni(e.target.value.replace(/\D/g, '').slice(0, 9))} placeholder="30123456" />
          </Field>
          <Field label="Teléfono">
            <input className="lb-input" inputMode="tel" value={telefono} onChange={e => setTelefono(e.target.value.replace(/[^\d\s+]/g, '').slice(0, 20))} placeholder="3544 123456" />
          </Field>
        </div>
        <Field label="Email">
          <input className="lb-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="vos@tuboliche.com" />
        </Field>
        <Field label="Contraseña">
          <input className="lb-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" />
        </Field>

        {err && <div className="lb-err" style={{ marginTop: 6 }}>{err}</div>}

        <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 10, lineHeight: 1.5 }}>
          Los datos de facturación (razón social, CUIT y domicilio) se cargan después, desde tu panel.
        </div>

        <button className="lb-btn lb-btn--primary lb-btn--block" style={{ marginTop: 16 }} onClick={submit} disabled={loading || !check?.available}>
          {loading ? 'Creando…' : 'Crear mi boliche →'}
        </button>

        {GOOGLE_CLIENT_ID && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0', color: 'var(--ink-600)', fontSize: 12 }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} /> o <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}><div ref={gbtn} /></div>
            <div style={{ fontSize: 11, color: 'var(--ink-600)', textAlign: 'center', marginTop: 8 }}>Con Google entrás verificado al instante. Completá nombre y subdominio primero.</div>
          </>
        )}

        <div style={{ fontSize: 12, color: 'var(--ink-600)', textAlign: 'center', marginTop: 18 }}>
          ¿Ya tenés cuenta? Entrá desde el subdominio de tu boliche.
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="lb-field"><div className="lb-label">{label}</div>{children}</div>;
}

// Pantalla de éxito del alta: le deja CLARA su URL de acceso (su subdominio) para que la
// guarde, avisa de la verificación por email y lo lleva a su panel por el subdominio.
function SignupDone({ slug, name, adminUrl, needsVerification, email }) {
  const shown = adminUrl.replace(/^https?:\/\//, '');
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 20px', textAlign: 'center' }}>
      <img src="/landing/tukipro-logo.svg" alt="TUKIPRO" style={{ height: 28, marginBottom: 28 }} />
      <div className="lb-display" style={{ fontSize: 30, color: '#fff' }}>¡{name} está listo! 🎉</div>
      {needsVerification && (
        <p style={{ color: 'var(--ink-700)', marginTop: 12, maxWidth: 440, lineHeight: 1.55 }}>
          Te enviamos un código de verificación a <b style={{ color: '#fff' }}>{email}</b>. Ingresalo la primera vez que entres para activar tu cuenta.
        </p>
      )}
      <div style={{ marginTop: 26, width: '100%', maxWidth: 440, background: 'var(--ink-100)', border: '1px solid var(--border)', borderRadius: 6, padding: '18px 20px' }}>
        <div className="lb-eyebrow" style={{ marginBottom: 8 }}>Esta es tu dirección de acceso — guardala</div>
        <div style={{ fontFamily: 'monospace', fontSize: 17, color: 'var(--red-400)', wordBreak: 'break-all', fontWeight: 700 }}>{shown}</div>
        <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 8 }}>Vas a entrar a tu panel siempre desde tu subdominio.</div>
      </div>
      <a href={adminUrl} className="lb-btn lb-btn--primary lb-btn--block" style={{ maxWidth: 440, marginTop: 18, textDecoration: 'none' }}>Entrar a mi panel →</a>
      <div style={{ color: 'var(--ink-600)', fontSize: 12, marginTop: 14 }}>Anotala o marcala como favorita: {shown}</div>
    </div>
  );
}
